import { useDispatch, useSelector } from 'react-redux'
import { useGetOrCreateOrderUUIDForEmployee } from 'contexts/WorkorderContext/use-get-or-create-order-uuid-for-employee'
import { IRootReducerState } from 'state/rootReducer'
import { useCallback } from 'react'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { useGetCurrentOpenOrder } from 'contexts/WorkorderContext/use-get-current-open-order'
import { createWorkorder } from 'app/workorder/state/workorder/actions'
import { isEmptyUUID } from '@cbgms/base/utils/uuid'
import { useGetOrCreateOrderUUIDForVehicle } from 'contexts/WorkorderContext/use-get-or-create-order-uuid-for-vehicle'
import { useGetOrCreateOrderUUIDForAccount } from 'contexts/WorkorderContext/use-get-or-create-order-uuid-for-account'

export interface DetermineWorkorderUUIDForContextOptions {
  mode?: WoResolverMode
  vehicleUUID?: string
  accountUUID?: string
}

export interface useDetermineWorkorderUUIDForContextBuilderOptions {
  onOpenDialog: (vehicleUUID?: string, accountUUID?: string) => Promise<string>
}

type DetermineWorkorderUUIDForContextFunc = (options: DetermineWorkorderUUIDForContextOptions) => Promise<string>
export enum WoResolverMode {
  ForceNew = 'force-new', // Always create a new workorder, even when already in workorder context
  CurrentContextOrCreateNew = 'create-new', // prefer to create a new workorder when not in workorder context
  CurrentContextOrReuseExisting = 'reuse-existing', // prefer current context, else find an existing order and otherwise create a new one
  CurrentContextOrUserSelect = 'user-select' // preffer current context, else allow user to select the prefered wo.
}

const useNewOrderCreator = () => {
  const dispatch = useDispatch()
  return useCallback(
    async (options: DetermineWorkorderUUIDForContextOptions) => {
      // when no vehicle or account uuid is passed, it will implicitly be an employee order
      const response = await dispatch(
        createWorkorder({ AccountUUID: options.accountUUID || undefined, VehicleUUID: options.vehicleUUID || undefined })
      )
      return response.Data.UUID
    },
    [dispatch]
  )
}

const useExistingOrNewOrderResolver = () => {
  const employeeUUID = useSelector((state: IRootReducerState) => state.auth.employee.UUID)
  const getVehicleOrderUUIDfunc = useGetOrCreateOrderUUIDForVehicle()
  const getAccountOrderUUIDfunc = useGetOrCreateOrderUUIDForAccount()
  const getEmployeeOrderUUIDfunc = useGetOrCreateOrderUUIDForEmployee()

  return useCallback(async (options: DetermineWorkorderUUIDForContextOptions) => {
    // try to find existing open orders
    // this is done based on priority of the input,
    // - prefer vehicle orders
    // - prefer account orders
    // - fallback to employee orders
    if (!isEmptyUUID(options.vehicleUUID)) {
      return getVehicleOrderUUIDfunc(options.vehicleUUID!, options.accountUUID)
    }
    if (!isEmptyUUID(options.accountUUID)) {
      return getAccountOrderUUIDfunc(options.accountUUID!, options.vehicleUUID)
    }
    return getEmployeeOrderUUIDfunc(employeeUUID)
  }, [])
}

/**
 * useDetermineWorkorderUUIDForContext will determine which workorder to use when outside of any order context
 * it might:
 *  - create a new order
 *  - re-use an open employee order
 *  - create a new order for a vehicle
 *  - ask the user: select an open order OR create a new one?
 *
 * optionally pass in a vehicle uuid if the you're in a vehicle context.
 */
export const useDetermineWorkorderUUIDForContext = ({
  onOpenDialog
}: useDetermineWorkorderUUIDForContextBuilderOptions): DetermineWorkorderUUIDForContextFunc => {
  const isPortal = useIsPortal()
  const newOrderCreator = useNewOrderCreator()
  const existingOrNewOrderResolver = useExistingOrNewOrderResolver()
  const currentOrder = useGetCurrentOpenOrder()

  const defaultMode: WoResolverMode = isPortal ? WoResolverMode.CurrentContextOrCreateNew : WoResolverMode.CurrentContextOrUserSelect

  return useCallback(
    (options: DetermineWorkorderUUIDForContextOptions) => {
      // By default if we're already in an order context, we can return the active workorder uuid
      if (currentOrder && options.mode !== WoResolverMode.ForceNew) {
        return new Promise(resolve => resolve(currentOrder))
      }
      // (GMS) defaults to a dialog to let user choose a workorder
      // (Portal) defaults to creating a new order. takes into accout the Vehicle/Account when creating new orders
      switch (options.mode || defaultMode) {
        case WoResolverMode.CurrentContextOrCreateNew:
        case WoResolverMode.ForceNew:
          return newOrderCreator(options)
        case WoResolverMode.CurrentContextOrReuseExisting:
          return existingOrNewOrderResolver(options)
        case WoResolverMode.CurrentContextOrUserSelect:
        default:
          return onOpenDialog(options.vehicleUUID, options.accountUUID)
      }
    },
    [defaultMode, currentOrder, newOrderCreator, existingOrNewOrderResolver, onOpenDialog]
  )
}
